import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { CreditCard, Settings, BookOpen, AlertTriangle } from "lucide-react";
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

/**
 * Navigation component for users without a valid paid subscription.
 * Shows only subscription management and help options.
 */
export function RestrictedNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleInternalNavigation = (event: React.MouseEvent<HTMLElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = event.target as HTMLElement;
    const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
    const href = anchor?.getAttribute("href");

    if (!href || !href.startsWith("/")) return;

    event.preventDefault();
    navigate(href);
  };
  
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };
  
  return (
    <NavigationMenu onClickCapture={handleInternalNavigation}>
      <NavigationMenuList className="flex items-center space-x-1">
        {/* Alert indicator */}
        <NavigationMenuItem>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-warning/10 text-warning border border-warning/20">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">
              {t('nav.subscriptionRequired', 'Subscription Required')}
            </span>
          </div>
        </NavigationMenuItem>

        {/* Subscription Link */}
        <NavigationMenuItem>
          <Link to="/settings/subscription">
            <div className={cn(
              "group px-5 py-2 rounded-full font-medium transition-all duration-200",
              "flex items-center gap-2",
              isActive("/settings/subscription")
                ? "bg-primary text-primary-foreground shadow-md"
                : "bg-primary/10 hover:bg-primary/20 text-primary"
            )}>
              <CreditCard className="h-4 w-4" />
              <span>{t('nav.choosePlan', 'Choose a Plan')}</span>
            </div>
          </Link>
        </NavigationMenuItem>

        {/* Help Menu */}
        <NavigationMenuItem>
          <NavigationMenuTrigger 
            className={cn(
              "group px-5 py-2 rounded-full font-medium transition-all duration-200",
              "flex items-center gap-2",
              isActive("/documentation")
                ? "bg-secondary text-secondary-foreground shadow-md"
                : "hover:bg-muted text-foreground"
            )}
          >
            <BookOpen className="h-4 w-4" />
            {t('nav.help', 'Help')}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="p-4 w-[300px]">
              <NavigationMenuLink asChild>
                <a
                  href="/documentation"
                  className="group block p-4 rounded-xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="p-2 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                      <BookOpen className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                  <div className="font-semibold text-sm mb-1">{t('nav.documentation', 'Documentation')}</div>
                  <p className="text-xs text-muted-foreground">{t('nav.documentationDesc', 'Learn how to use the platform')}</p>
                </a>
              </NavigationMenuLink>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
